import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon, UserAvatar } from '@repo/atomic-components';
import { UserData } from '@/api';
import { classNames } from 'primereact/utils';

interface AssignUserPopupProps {
  /** Anchor row rect, in viewport coordinates. The popup prefers to open
   * below `anchor.bottom` and flips above `anchor.top` when there isn't
   * room (e.g. row is near the bottom of the viewport). Aligns its left
   * edge with `anchor.left`. */
  anchor: { top: number; bottom: number; left: number };
  /** All assignable users. */
  usersList: UserData[];
  /** Currently assigned user id, if any — used to highlight the row. */
  currentUserId?: string | null;
  /** Called with the selected user. The popup also closes on selection. */
  onSelect: (user: UserData) => void;
  /** Called when the user clicks outside, presses Escape, or clears the
   * assignment via the "Unassign" row. */
  onClose: () => void;
  /** Optional unassign handler — when supplied, an "Unassign" row is shown
   * at the top of the list; clicking it calls this and closes the popup. */
  onUnassign?: () => void;
}

const GAP = 4; // px between popup and anchor
const VIEWPORT_MARGIN = 24; // min distance from viewport edges
// List sizing — `LIST_MAX_HEIGHT` caps the scroll viewport (so the popup
// fits in 400px overall with the search row above), and `ITEM_HEIGHT` is
// the fixed row height the virtualiser uses to compute offsets.
const LIST_MAX_HEIGHT = 280;
const ITEM_HEIGHT = 44;
const OVERSCAN = 4;

/**
 * Compact searchable user picker — modelled on MedicalRecordsPage's
 * ProviderSelectorPopup. List is virtualised so even very large user
 * directories stay snappy. Filter matches on both name and email.
 */
export const AssignUserPopup: React.FC<AssignUserPopupProps> = memo(
  ({ anchor, usersList, currentUserId, onSelect, onClose, onUnassign }) => {
    const [searchQuery, setSearchQuery] = useState('');
    // Highlighted row for arrow-key navigation. Default 0 so Enter on a
    // fresh popup picks the first user without needing a Down press
    // first. Reset whenever the filter changes — the previous index
    // might no longer exist in the narrowed list.
    const [focusedIndex, setFocusedIndex] = useState(0);
    const popupRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listScrollRef = useRef<HTMLDivElement>(null);
    // Computed viewport coords. Set in useLayoutEffect after the popup
    // mounts and we can measure its actual height — that's what lets us
    // decide whether to flip above the anchor.
    const [resolvedPos, setResolvedPos] = useState<{ top: number; left: number } | null>(null);

    // Filter on both name and email. Trims the query so trailing spaces
    // don't kill matches; empty query returns the full list.
    const filtered = useMemo(() => {
      const query = searchQuery.trim().toLowerCase();
      if (!query) return usersList;
      return usersList.filter(
        (u) => u.userName.toLowerCase().includes(query) || u.userEmail.toLowerCase().includes(query),
      );
    }, [usersList, searchQuery]);

    // Reset highlight to the first row whenever the filtered list changes
    // (typing in the search box) so Enter always picks the top match.
    useEffect(() => {
      setFocusedIndex(filtered.length > 0 ? 0 : -1);
    }, [filtered]);

    // Keep the highlighted row in view while arrow-key navigating —
    // otherwise pressing Down past the visible window would move focus
    // off-screen until the user manually scrolled.
    useEffect(() => {
      if (!listScrollRef.current || focusedIndex < 0) return;
      const itemTop = focusedIndex * ITEM_HEIGHT;
      const itemBottom = itemTop + ITEM_HEIGHT;
      const viewTop = listScrollRef.current.scrollTop;
      const viewBottom = viewTop + listScrollRef.current.clientHeight;
      if (itemTop < viewTop) {
        listScrollRef.current.scrollTop = itemTop;
      } else if (itemBottom > viewBottom) {
        listScrollRef.current.scrollTop = itemBottom - listScrollRef.current.clientHeight;
      }
    }, [focusedIndex]);

    // ---------- Virtual scrolling ----------
    const [scrollTop, setScrollTop] = useState(0);
    const handleListScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
      setScrollTop(event.currentTarget.scrollTop);
    }, []);
    const startIndex = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - OVERSCAN);
    const endIndex = Math.min(filtered.length, Math.ceil((scrollTop + LIST_MAX_HEIGHT) / ITEM_HEIGHT) + OVERSCAN);
    const visibleItems = useMemo(() => {
      const items: { user: UserData; offsetTop: number }[] = [];
      for (let i = startIndex; i < endIndex; i++) {
        items.push({ user: filtered[i], offsetTop: i * ITEM_HEIGHT });
      }
      return items;
    }, [filtered, startIndex, endIndex]);
    const totalListHeight = filtered.length * ITEM_HEIGHT;

    // Reset list scroll when the search narrows the result count below
    // where we used to be — otherwise the spacer would size to nothing
    // and we'd be staring at an empty viewport.
    useEffect(() => {
      if (listScrollRef.current && scrollTop > totalListHeight) {
        listScrollRef.current.scrollTop = 0;
        setScrollTop(0);
      }
    }, [totalListHeight, scrollTop]);

    useLayoutEffect(() => {
      if (!popupRef.current) return;
      const rect = popupRef.current.getBoundingClientRect();
      const popupHeight = rect.height;
      const popupWidth = rect.width;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      const spaceBelow = viewportHeight - anchor.bottom - GAP - VIEWPORT_MARGIN;
      const spaceAbove = anchor.top - GAP - VIEWPORT_MARGIN;
      let top: number;
      if (popupHeight <= spaceBelow || spaceBelow >= spaceAbove) {
        top = anchor.bottom + GAP;
      } else {
        top = anchor.top - GAP - popupHeight;
      }
      top = Math.max(VIEWPORT_MARGIN, Math.min(top, viewportHeight - popupHeight - VIEWPORT_MARGIN));

      let left = anchor.left;
      if (left + popupWidth > viewportWidth - VIEWPORT_MARGIN) {
        left = viewportWidth - popupWidth - VIEWPORT_MARGIN;
      }
      left = Math.max(VIEWPORT_MARGIN, left);

      setResolvedPos({ top, left });
    }, [anchor.top, anchor.bottom, anchor.left]);

    // Focus the search input as soon as the popup becomes visible. Doing
    // this in a plain on-mount effect would fire while the popup is still
    // `visibility: hidden` (we hide it for one layout pass to measure
    // placement), and hidden elements can't accept focus — so the input
    // would silently stay unfocused and the user couldn't start typing.
    useEffect(() => {
      if (resolvedPos) {
        inputRef.current?.focus();
      }
    }, [resolvedPos]);

    useEffect(() => {
      const handlePointerDown = (event: globalThis.MouseEvent) => {
        const target = event.target as Node | null;
        if (target && popupRef.current && !popupRef.current.contains(target)) {
          onClose();
        }
      };
      const handleKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape') onClose();
      };
      document.addEventListener('mousedown', handlePointerDown);
      document.addEventListener('keydown', handleKey);
      return () => {
        document.removeEventListener('mousedown', handlePointerDown);
        document.removeEventListener('keydown', handleKey);
      };
    }, [onClose]);

    return createPortal(
      <div
        ref={popupRef}
        style={{
          position: 'fixed',
          top: resolvedPos?.top ?? anchor.bottom + GAP,
          left: resolvedPos?.left ?? anchor.left,
          visibility: resolvedPos ? 'visible' : 'hidden',
          // Sit above PrimeReact Sidebar (root ~1101) so the popup doesn't
          // get hidden behind a sidebar when the trigger lives inside one.
          zIndex: 1200,
        }}
        className="border-fletchers-gray-110 flex max-h-[400px] w-[480px] flex-col rounded-lg border bg-white shadow-lg"
      >
        {/* Search row */}
        <div className="border-fletchers-gray-110 flex flex-col gap-3 border-b px-3 py-3">
          <div className="relative">
            <i className="pi pi-search absolute top-1/2 left-[10px] -translate-y-1/2 text-[14px] text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search by name or email"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  if (filtered.length === 0) return;
                  setFocusedIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
                } else if (event.key === 'ArrowUp') {
                  event.preventDefault();
                  if (filtered.length === 0) return;
                  setFocusedIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1));
                } else if (event.key === 'Enter') {
                  event.preventDefault();
                  const user = filtered[focusedIndex];
                  if (user) {
                    onSelect(user);
                    onClose();
                  }
                }
              }}
              className="border-fletchers-gray-110 placeholder:text-fletchers-gray-200 h-8 w-full rounded-[4px] border bg-white pr-8 pl-[35px] text-[14px] leading-5 text-black focus:outline-none"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute top-1/2 right-[10px] -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600"
              >
                <CloseIcon width={10} height={10} />
              </button>
            )}
          </div>
        </div>

        {/* Optional Unassign row — sits above the virtualised list so its
            height doesn't have to fit in the row grid. Only visible when
            no search is active to avoid surprising users mid-filter. */}
        {onUnassign && !searchQuery.trim() && (
          <button
            type="button"
            onClick={() => {
              onUnassign();
              onClose();
            }}
            className="hover:bg-fletchers-gray-25 border-fletchers-gray-110 flex cursor-pointer items-center gap-2 border-b px-3 py-2 text-left"
          >
            <UserAvatar unassigned size={24} />
            <span className="text-fletchers-gray-200 text-sm italic">Unassigned</span>
          </button>
        )}

        {/* Virtualised list — only rows in the visible window (plus a small
            overscan) are mounted in the DOM. The spacer div takes the
            full computed list height so the scrollbar geometry stays
            correct as if every row were rendered. */}
        {filtered.length === 0 ? (
          <div className="text-fletchers-gray-200 px-3 py-3 text-center text-sm">No users found</div>
        ) : (
          <div
            ref={listScrollRef}
            className="overflow-y-auto rounded-b-lg"
            style={{ maxHeight: LIST_MAX_HEIGHT }}
            onScroll={handleListScroll}
          >
            <div style={{ height: totalListHeight, position: 'relative' }}>
              {visibleItems.map(({ user, offsetTop }) => {
                const itemIndex = Math.round(offsetTop / ITEM_HEIGHT);
                const isKeyboardFocused = itemIndex === focusedIndex;
                const isCurrent = user.userId === currentUserId;
                return (
                  <button
                    key={user.userId}
                    type="button"
                    onClick={() => {
                      onSelect(user);
                      onClose();
                    }}
                    onMouseEnter={() => setFocusedIndex(itemIndex)}
                    style={{ position: 'absolute', top: offsetTop, left: 0, right: 0, height: ITEM_HEIGHT }}
                    className={classNames(
                      'flex w-full cursor-pointer items-center gap-2 px-3 text-left',
                      // Keyboard focus wins over the "current assignee"
                      // tint so users always see where Enter will land.
                      isKeyboardFocused
                        ? 'bg-fletchers-gray-50'
                        : isCurrent
                          ? 'bg-fletchers-green-50'
                          : 'hover:bg-fletchers-gray-25',
                    )}
                    title={`${user.userName} (${user.userEmail})`}
                  >
                    <UserAvatar name={user.userName} size={24} fontSize={10} />
                    <div className="flex min-w-0 flex-1 items-baseline gap-2 overflow-hidden">
                      <span className="text-fletchers-black-900 shrink-0 text-sm">
                        {user.userName.length > 60 ? `${user.userName.slice(0, 60)}…` : user.userName}
                      </span>
                      <span className="text-fletchers-gray-200 truncate text-sm">{user.userEmail}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>,
      document.body,
    );
  },
);
